import type { SourceId } from "@/core/model/ids.ts";
import type { ImageSource, VideoSource } from "@/core/model/source.ts";
import { readImageFacts } from "./imageFacts.ts";
import { detectMediaKind } from "./kind.ts";

export class UnsupportedMediaError extends Error {
  constructor(fileName: string) {
    super(`対応していないファイルです: ${fileName}`);
    this.name = "UnsupportedMediaError";
  }
}

export class MediaProbeError extends Error {
  constructor(fileName: string, reason: string) {
    super(`ファイルを読み取れませんでした (${fileName}): ${reason}`);
    this.name = "MediaProbeError";
  }
}

type Identity = {
  readonly id: SourceId;
  readonly name: string;
  readonly importedAt: string;
};

export async function probeImage(file: File, identity: Identity): Promise<ImageSource> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (error) {
    throw new MediaProbeError(file.name, error instanceof Error ? error.message : "デコード失敗");
  }
  try {
    const facts = await readImageFacts(file, bitmap);
    return {
      kind: "image",
      id: identity.id,
      name: identity.name,
      mimeType: file.type,
      sizeBytes: file.size,
      importedAt: identity.importedAt,
      widthPx: bitmap.width,
      heightPx: bitmap.height,
      ...facts,
    };
  } finally {
    bitmap.close();
  }
}

/**
 * Reads container metadata through a detached <video>. It gives duration and pixel size
 * without decoding a frame; frame rate is not exposed, so it is filled in later by the
 * decoder that builds the proxy.
 *
 * A media element can also simply never answer — a codec the browser will not touch, or
 * a tab that was in the background when the load started, both leave it loading with no
 * error to catch. The timeout is what turns that into something the caller can report.
 */
const PROBE_TIMEOUT_MS = 15_000;

export function probeVideo(file: File, identity: Identity): Promise<VideoSource> {
  return new Promise((resolve, reject) => {
    const element = document.createElement("video");
    const url = URL.createObjectURL(file);
    const timer = setTimeout(() => {
      cleanup();
      reject(
        new MediaProbeError(
          file.name,
          "この動画の情報を読み取れませんでした。タブを開いたまま、もう一度お試しください",
        ),
      );
    }, PROBE_TIMEOUT_MS);
    const cleanup = (): void => {
      clearTimeout(timer);
      element.removeAttribute("src");
      URL.revokeObjectURL(url);
    };

    element.preload = "metadata";
    element.addEventListener(
      "loadedmetadata",
      () => {
        const source: VideoSource = {
          kind: "video",
          id: identity.id,
          name: identity.name,
          mimeType: file.type,
          sizeBytes: file.size,
          importedAt: identity.importedAt,
          durationSec: Number.isFinite(element.duration) ? element.duration : 0,
          widthPx: element.videoWidth,
          heightPx: element.videoHeight,
          frameRate: 0,
          hasAudio: true,
        };
        cleanup();
        resolve(source);
      },
      { once: true },
    );
    element.addEventListener(
      "error",
      () => {
        cleanup();
        reject(new MediaProbeError(file.name, "メタデータを読めませんでした"));
      },
      { once: true },
    );
    element.src = url;
  });
}

export function assertSupported(file: File): "image" | "audio" | "video" {
  const kind = detectMediaKind(file);
  if (!kind) throw new UnsupportedMediaError(file.name);
  return kind;
}
